<template>
  <div class="path-container">
    <!-- 标题栏 -->
    <div class="section-header">
      <span class="section-tag">05</span>
      <h2 class="section-title">选手出路</h2>
    </div>

    <!-- 主体内容：左侧大图 + 右侧赛事播放 -->
    <div class="main-content">
      <!-- 左侧大图 -->
      <div class="banner-wrap">
        <img :src="props.data.bannerImg" alt="选手活动合影" class="banner-img" />
      </div>

      <!-- 右侧赛事播放模块（加背景色+右下角图标） -->
      <div class="broadcast-module">
        <h3 class="broadcast-title">{{ props.data.broadcastPlatforms.title }}</h3>
        <div class="platform-grid">
          <div class="platform-col" v-for="(col, idx) in props.data.broadcastPlatforms.lists" :key="idx">
            <span class="platform-item" v-for="item in col" :key="item">{{ item }}</span>
          </div>
        </div>
        <!-- 右下角功能图标 -->
        <div class="function-icons">
          <i class="icon icon-mic">🎤</i>
          <i class="icon icon-speaker">📢</i>
          <i class="icon icon-camera">📹</i>
        </div>
      </div>
    </div>

    <!-- 下方分类模块（加背景色区分） -->
    <div class="path-grid">
      <div class="path-item" v-for="(item, idx) in props.data.pathItems" :key="idx">
        <h3 class="item-title" :style="{ color: props.theme.primaryColor }">{{ item.title }}</h3>
        <p class="item-desc">{{ item.desc }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps } from 'vue';

const props = defineProps({
  data: { type: Object, required: true },
  theme: { type: Object, required: true }
});
</script>

<style scoped lang="scss">
// 核心变量
$primary: v-bind('props.theme.primaryColor');
$bg-white: #FFFFFF;
$bg-broadcast: #FFF9FC; // 赛事播放模块背景色（浅粉，柔和区分）
$bg-path: #F8FAFC;      // 下方分类模块背景色（浅蓝灰，柔和区分）
$text-dark: #333;
$text-gray: #666;
$border-light: #F5F5F5;
$radius: 8px;
$shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

// 外层容器
.path-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

// 标题栏
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 32px;

  .section-tag {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: $primary;
    color: white;
    font-size: 18px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
  }

  .section-title {
    font-size: 24px;
    color: $text-dark;
    margin: 0;
  }
}

// 主体内容布局
.main-content {
  display: flex;
  gap: 24px;
  margin-bottom: 32px;
  flex-wrap: wrap;
}

// 左侧大图
.banner-wrap {
  flex: 2;
  min-width: 300px;
  border-radius: $radius;
  overflow: hidden;
  box-shadow: $shadow;

  .banner-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

// 右侧赛事播放模块（加背景色+图标）
.broadcast-module {
  flex: 3;
  min-width: 300px;
  background: $bg-broadcast; // 浅粉背景区分
  border: 1px solid $border-light;
  border-radius: $radius;
  padding: 24px;
  position: relative;
  box-shadow: $shadow;

  .broadcast-title {
    font-size: 18px;
    color: $primary;
    margin-bottom: 20px;
    font-weight: 600;
  }

  .platform-grid {
    display: flex;
    gap: 40px;
    flex-wrap: wrap;
  }

  .platform-col {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .platform-item {
    font-size: 14px;
    color: $text-gray;
    line-height: 1.8;
    transition: color 0.2s ease;

    &:hover {
      color: $primary;
    }
  }

  // 右下角功能图标
  .function-icons {
    position: absolute;
    right: 24px;
    bottom: 24px;
    display: flex;
    gap: 16px;

    .icon {
      font-size: 20px;
      color: $primary;
      opacity: 0.7;
      transition: all 0.2s ease;
      cursor: default;

      &:hover {
        opacity: 1;
        transform: scale(1.1);
      }
    }
  }
}

// 下方分类模块（加背景色区分）
.path-grid {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  .path-item {
    flex: 1;
    min-width: 220px;
    background: $bg-path; // 浅蓝灰背景区分
    border: 1px solid $border-light;
    border-radius: $radius;
    padding: 24px;
    box-shadow: $shadow;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-3px);
    }

    .item-title {
      font-size: 16px;
      margin-bottom: 12px;
      font-weight: 600;
    }

    .item-desc {
      font-size: 14px;
      color: $text-gray;
      line-height: 1.8;
      white-space: pre-line; // 保留换行
      margin: 0;
    }
  }
}

// 响应式适配
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }

  .path-grid {
    flex-direction: column;
  }

  .platform-grid {
    gap: 20px;
  }

  .function-icons {
    gap: 12px;
    font-size: 18px;
  }
}
</style>